import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import Layout from "../components/layout/Esign";
import config from '../config.js';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem
} from '@mui/material';
import {
  Description,
  PendingActions,
  CheckCircleOutline,
  Warning,
  Delete,
  Edit,
  Add
} from '@mui/icons-material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const EsignDashboard = () => {
  const [documents, setDocuments] = useState([]);
  const {API_BASE_URL} = config
  const [workflows, setWorkflows] = useState({ pending: [], inProgress: [], completed: [] });
  const [loading, setLoading] = useState(true);
  const [openWorkflowDialog, setOpenWorkflowDialog] = useState(false);
  const [newWorkflow, setNewWorkflow] = useState({
    title: '',
    document: '',
    assignee: '',
    deadline: '',
    priority: 'Medium',
    status: 'pending'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [docsResponse, workflowsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/v1/getUserEDocument`),
        axios.get(`${API_BASE_URL}/api/v1/Workflow`)
      ]);

      setDocuments(docsResponse.data.data);
      setWorkflows(workflowsResponse.data.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const handleCreateWorkflow = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/v1/Workflow`, newWorkflow);
      setOpenWorkflowDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error creating workflow:', error);
    }
  };

  const handleDeleteWorkflow = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/v1/workflow/${id}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting workflow:', error);
    }
  };

  const statsData = [
    {
      title: 'Pending Documents',
      count: documents.filter(doc => doc.status === 'PENDING').length,
      icon: <PendingActions />,
      color: '#FF9800'
    },
    {
      title: 'Signed Documents',
      count: documents.filter(doc => doc.status === 'SIGNED').length,
      icon: <CheckCircleOutline />,
      color: '#4CAF50'
    },
    {
      title: 'Failed Documents',
      count: documents.filter(doc => doc.status === 'FAILED').length,
      icon: <Warning />,
      color: '#F44336'
    }
  ];

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Layout>
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={{
          visible: {
            transition: {
              staggerChildren: 0.1
            }
          }
        }}
      >
        <Grid container spacing={3}>
          {statsData.map((stat, index) => (
            <Grid item xs={12} md={4} key={index}>
              <motion.div variants={cardVariants}>
                <Card sx={{ minHeight: 140, borderLeft: `4px solid ${stat.color}` }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="h6">{stat.title}</Typography>
                      <IconButton sx={{ color: stat.color }}>{stat.icon}</IconButton>
                    </Box>
                    <Typography variant="h3" sx={{ mt: 2 }}>{stat.count}</Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        <motion.div variants={cardVariants} style={{ marginTop: '2rem' }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Recent Documents</Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setOpenWorkflowDialog(true)}
                >
                  New Workflow
                </Button>
              </Box>

              <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Document Serial</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>WhatsApp Number</TableCell>
                      <TableCell>Created At</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {documents.slice(0, 5).map((doc) => (
                      <TableRow key={doc._id}>
                        <TableCell>{doc.documentSerialNo}</TableCell>
                        <TableCell>
                          <Typography
                            sx={{
                              color:
                                doc.status === 'SIGNED'
                                  ? 'success.main'
                                  : doc.status === 'PENDING'
                                  ? 'warning.main'
                                  : 'error.main'
                            }}
                          >
                            {doc.status}
                          </Typography>
                        </TableCell>
                        <TableCell>{doc.whatsappNumber}</TableCell>
                        <TableCell>{new Date(doc.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell align="right">
                          <IconButton onClick={() => handleDeleteWorkflow(doc._id)}>
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>

      <Dialog open={openWorkflowDialog} onClose={() => setOpenWorkflowDialog(false)}>
        <DialogTitle>Create New Workflow</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Title"
              value={newWorkflow.title}
              onChange={(e) => setNewWorkflow({ ...newWorkflow, title: e.target.value })}
            />
            <TextField
              label="Document URL"
              value={newWorkflow.document}
              onChange={(e) => setNewWorkflow({ ...newWorkflow, document: e.target.value })}
            />
            <TextField
              label="Assignee"
              value={newWorkflow.assignee}
              onChange={(e) => setNewWorkflow({ ...newWorkflow, assignee: e.target.value })}
            />
            <TextField
              type="date"
              label="Deadline"
              InputLabelProps={{ shrink: true }}
              value={newWorkflow.deadline}
              onChange={(e) => setNewWorkflow({ ...newWorkflow, deadline: e.target.value })}
            />
            <TextField
              select
              label="Priority"
              value={newWorkflow.priority}
              onChange={(e) => setNewWorkflow({ ...newWorkflow, priority: e.target.value })}
            >
              <MenuItem value="High">High</MenuItem>
              <MenuItem value="Medium">Medium</MenuItem>
              <MenuItem value="Low">Low</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenWorkflowDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateWorkflow} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>
    </Container>
    </Layout>
  );
};

export default EsignDashboard;
